import { motion, useMotionValue, useReducedMotion, useScroll, useSpring, useTransform } from "framer-motion";
import { useRef } from "react";
import { hero } from "@/content/copy";
import { scrollToId } from "@/lib/scroll";
import { Eyebrow, Owl } from "./primitives";
import { OrbitText } from "./OrbitText";

const ease = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, i }) => (
  <span className="block overflow-hidden pb-[0.2em] -mb-[0.2em]">
    <motion.span className="block" initial={{ y: "105%" }} animate={{ y: "0%" }} transition={{ duration: 1.1, ease, delay: 0.25 + i * 0.14 }}>
      {children}
    </motion.span>
  </span>
);

const useTilt = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };
  return { rx, ry, onMove, onLeave };
};

const HeroOwl = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 120]);
  const { rx, ry, onMove, onLeave } = useTilt();
  return (
    <div ref={ref} className="ph-hero-owl-wrap" onPointerMove={reduce ? undefined : onMove} onPointerLeave={onLeave}>
      <motion.div style={{ y }} className="relative w-full h-full flex items-center justify-center">
        <OrbitText />
        <motion.div style={{ rotateX: rx, rotateY: ry, transformPerspective: 900 }} className="relative z-10 w-[72%]"
          initial={{ opacity: 0, scale: 0.86, rotate: -8 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} transition={{ duration: 1.4, ease, delay: 0.1 }}>
          <motion.div animate={reduce ? undefined : { y: [0, -10, 0], rotate: [0, -2, 0] }} transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}>
            <Owl data-testid="hero-owl-image" fetchPriority="high" className="w-full h-auto select-none ph-owl-shadow" />
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
};

const go = (e, href) => { e.preventDefault(); scrollToId(href); };

export const Hero = () => (
  <section id="top" data-testid="hero-section" className="relative pt-24 sm:pt-28 lg:pt-32 pb-16 sm:pb-24 overflow-hidden">
    <div className="ph-container grid lg:grid-cols-12 gap-8 lg:gap-6 items-center">
      <div className="order-1 lg:order-2 lg:col-span-5"><HeroOwl /></div>
      <div className="order-2 lg:order-1 lg:col-span-7">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.1 }}>
          <Eyebrow>{hero.eyebrow}</Eyebrow>
        </motion.div>
        <h1 data-testid="hero-headline" className="ph-h1 mt-5 sm:mt-7">
          {hero.lines.map((l, i) => (
            <MaskedLine key={l} i={i}>{i === 1 ? <em className="ph-italic">{l}</em> : l}</MaskedLine>
          ))}
        </h1>
        <motion.p data-testid="hero-subtitle" className="ph-lead mt-7 sm:mt-9 max-w-xl" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease, delay: 0.65 }}>
          {hero.body}
        </motion.p>
        <motion.div className="mt-9 sm:mt-11 flex flex-col sm:flex-row gap-3 sm:gap-4" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease, delay: 0.8 }}>
          <a href="#founding-question" onClick={(e) => go(e, "#founding-question")} data-testid="hero-btn-founding-question" className="ph-btn ph-btn-solid justify-center">{hero.primary}<span aria-hidden="true" className="ph-btn-icon">↓</span></a>
          <a href="#goal" onClick={(e) => go(e, "#goal")} data-testid="hero-btn-meet" className="ph-btn ph-btn-ghost justify-center">{hero.secondary}</a>
        </motion.div>
      </div>
    </div>
  </section>
);
